import isequal from 'lodash.isequal';

import { deepClone } from 'other/helpers';
import { initialMapAreas } from './mapOptionsInitialState';
import { isEnvLayer, isWindyLayer } from 'pages/Map/utils/helpers';

import {
  EMapArea,
  EMapAreaAction,
  EMapLayer,
  EProtectedZone,
  TMapAreas
} from 'types';
import { TMapOptionsState } from './mapOptionsModel';

/**
 *
 */
function isInitial(areas: TMapAreas): boolean {
  const { zones: a, ..._areas } = areas;
  const { zones: b, ..._initials } = initialMapAreas;
  return isequal(_areas, _initials);
}

/**
 *
 */
function isOnlyOSM(areas: TMapAreas): boolean {
  const { zones, ..._areas } = areas;
  return isequal(_areas, {
    base: EMapArea.OSM,
    feature: null,
    top: []
  });
}

/**
 *
 */
function removeArea(areas: EMapArea[], area: EMapArea): EMapArea[] {
  return areas.filter((a: EMapArea) => a !== area);
}

/**
 *
 */
function checkTop(areas: TMapAreas, _update: Partial<TMapAreas>): TMapAreas {
  const update = { ...areas, ..._update };
  if (
    Array.isArray(update.base) ||
    Array.isArray(update.feature) ||
    !Array.isArray(update.top) ||
    !Array.isArray(update.zones)
  ) {
    window.console.error('Bad data type');
  }

  return {
    ...update,
    top: Array.from(new Set(update.top)) as any
  };
}

/**
 *
 */
function handleTopLevelArea(
  areas: Readonly<TMapAreas>,
  area: EMapArea,
  action: EMapAreaAction
): TMapAreas {
  const top =
    action === EMapAreaAction.ADD
      ? [...areas.top, area]
      : removeArea(areas.top, area);
  return checkTop(areas, { top } as any);
}

/**
 *
 */
export function checkEEZ(areas: TMapAreas): TMapAreas {
  const { zones, ..._areas } = areas;
  const isIncomplete = isequal(_areas, {
    base: EMapArea.OSM,
    feature: null,
    top: [EMapArea.EEZ_LINES]
  });

  let result = isIncomplete ? initialMapAreas : areas;

  if (
    (result.base !== EMapArea.OSM &&
      areas.feature === EMapArea.EEZ_AREAS &&
      !areas.top.includes(EMapArea.EEZ_LINES)) ||
    (areas.feature === EMapArea.EEZ_AREAS &&
      !areas.top.includes(EMapArea.EEZ_LINES) &&
      areas.top.length > 0) ||
    (areas.feature === EMapArea.EEZ_AREAS &&
      !areas.top.includes(EMapArea.EEZ_LINES))
  ) {
    result = {
      ...result,
      feature: null,
      top: [...result.top, EMapArea.EEZ_LINES] as any
    };
  }

  return { ...result, zones: areas.zones };
}

/**
 *
 */
export function checkEEZ2(areas: TMapAreas, action: EMapAreaAction): TMapAreas {
  if (
    areas.feature === EMapArea.DEPTH_CURVES_COLOUR &&
    areas.top.includes(EMapArea.EEZ_LINES)
  ) {
    areas.top = [
      ...removeArea(areas.top, EMapArea.EEZ_LINES),
      EMapArea.EEZ_LINES_ALT
    ] as any;
  } else if (
    action !== EMapAreaAction.ADD &&
    areas.feature !== EMapArea.DEPTH_CURVES_COLOUR &&
    areas.top.includes(EMapArea.EEZ_LINES_ALT)
  ) {
    areas.top = [
      ...removeArea(areas.top, EMapArea.EEZ_LINES_ALT),
      EMapArea.EEZ_LINES
    ] as any;
  }
  return areas;
}

/**
 *
 */
export function handleZones(
  areas: Readonly<TMapAreas>,
  zones: Array<EProtectedZone>,
  action: EMapAreaAction
): TMapAreas {
  const update = deepClone(areas) as any;

  if (action === EMapAreaAction.ADD) {
    update.zones = [...update.zones, ...zones];
  } else {
    update.zones = update.zones.filter(
      (z: EProtectedZone) => !zones.includes(z)
    );
  }

  return checkTop(areas, update);
}

/**
 *
 */
export function checkAreas(areas: TMapAreas, layer: EMapLayer): TMapAreas {
  const result = deepClone(areas);

  if (isWindyLayer(layer) && areas.base === EMapArea.OSM) {
    result.base = null;
  }

  if (
    areas.feature === EMapArea.EEZ_AREAS &&
    (isEnvLayer(layer) || isWindyLayer(layer))
  ) {
    result.feature = null;
    result.top = [...result.top, EMapArea.EEZ_LINES] as any;
  }

  if (isEnvLayer(layer) && areas.feature === null && areas.base === null) {
    result.base = EMapArea.OSM;
  }

  return result;
}

/**
 *
 */
export function handleChoice(
  area: EMapArea,
  action: EMapAreaAction,
  mapOptions: TMapOptionsState
) {
  const choices = deepClone(mapOptions.mapAreaChoice);

  if (action === EMapAreaAction.TOGGLE) {
    if (area === EMapArea.SEABED_COLOUR) {
      choices.isForceBWSeabed =
        mapOptions.mapAreas.base === EMapArea.SEABED_COLOUR;
    }
    if (area === EMapArea.DEPTH_CURVES_COLOUR) {
      choices.isForceBWDepth =
        mapOptions.mapAreas.feature === EMapArea.DEPTH_CURVES_COLOUR;
    }
  }

  return choices;
}

/**
 *
 */
export function resetLayers(mapOptions: TMapOptionsState) {
  const { mapAreaChoice, mapAreas } = mapOptions;
  let update = deepClone(mapAreas);

  if (!update.base) {
    update.base = EMapArea.OSM;
  }

  if (
    !update.feature &&
    update.base === EMapArea.OSM &&
    update.top.length === 1 &&
    update.top.includes(EMapArea.EEZ_LINES)
  ) {
    update.feature = EMapArea.EEZ_AREAS;
    update.top = [];
  }

  if (
    update.top.includes(EMapArea.DEPTH_CURVES_BW) &&
    !mapAreaChoice?.isForceBWDepth
  ) {
    update.feature = EMapArea.DEPTH_CURVES_COLOUR;
    update = handleTopLevelArea(
      update,
      EMapArea.DEPTH_CURVES_BW,
      EMapAreaAction.REMOVE
    ) as any;
  }

  if (update.base === EMapArea.SEABED_BW && !mapAreaChoice?.isForceBWSeabed) {
    update.base = EMapArea.SEABED_COLOUR;
  }

  return update;
}

/**
 *
 */
export function handleAreas(
  mapAreas: Readonly<TMapAreas>,
  area: EMapArea,
  action: EMapAreaAction
): TMapAreas {
  const areas = deepClone(mapAreas);
  let update = null;

  if (area === EMapArea.ICES) {
    /**
     * ICES
     */
    update = handleTopLevelArea(areas, EMapArea.ICES, action);
  } else if (area === EMapArea.EEZ_LINES) {
    /**
     * EEZ_LINES
     */
    if (action === EMapAreaAction.ADD) {
      if (isOnlyOSM(areas)) {
        update = { feature: EMapArea.EEZ_AREAS };
      } else {
        update = {
          top: [...areas.top, area]
        };
      }
    } else {
      if (isInitial(areas)) {
        update = { feature: null };
      } else {
        update = {
          top: removeArea(removeArea(areas.top, area), EMapArea.EEZ_LINES_ALT)
        };
      }
    }
    update = checkTop(areas, update);
  } else if (area === EMapArea.SEABED_BW) {
    /**
     * SEABED_BW
     */
    if (action === EMapAreaAction.ADD) {
      update = { base: EMapArea.SEABED_BW };
      if (areas.feature === EMapArea.EEZ_AREAS) {
        update = {
          base: EMapArea.SEABED_BW,
          feature: null,
          top: [...areas.top, EMapArea.EEZ_LINES]
        };
      }
    } else if (action === EMapAreaAction.REMOVE) {
      update = { base: EMapArea.OSM };
    } else {
      update = { base: EMapArea.SEABED_COLOUR };
    }
    update = checkTop(areas, update);
  } else if (area === EMapArea.SEABED_COLOUR) {
    /**
     * SEABED_COLOUR
     */
    if (action === EMapAreaAction.ADD) {
      update = { base: EMapArea.SEABED_COLOUR };
    } else if (action === EMapAreaAction.REMOVE) {
      update = { base: EMapArea.OSM };
    } else {
      update = {
        base:
          areas.base === EMapArea.SEABED_BW
            ? EMapArea.SEABED_COLOUR
            : EMapArea.SEABED_BW
      };
    }
    update = checkTop(areas, update);
  } else if (area === EMapArea.DEPTH_CURVES_BW) {
    /**
     * DEPTH_CURVES_BW
     */
    if (action === EMapAreaAction.ADD) {
      update = { top: [...areas.top, EMapArea.DEPTH_CURVES_BW] };
      if (areas.feature === EMapArea.EEZ_AREAS) {
        update = {
          feature: null,
          top: [EMapArea.DEPTH_CURVES_BW, EMapArea.EEZ_LINES]
        };
      }
    } /*if (action === EMapAreaAction.REMOVE)*/ else {
      // Remove
      update = {
        feature: null,
        top: removeArea(areas.top, area)
      };
    }
    update = checkTop(areas, update);
  } else if (area === EMapArea.DEPTH_CURVES_COLOUR) {
    /**
     * DEPTH_CURVES_COLOUR
     */
    if (action === EMapAreaAction.ADD) {
      update = { feature: EMapArea.DEPTH_CURVES_COLOUR };
      if (areas.feature === EMapArea.EEZ_AREAS) {
        update = {
          ...update,
          top: [...areas.top, EMapArea.EEZ_LINES_ALT]
        };
      }
    } else if (action === EMapAreaAction.REMOVE) {
      update = { feature: null };
      if (areas.top.includes(EMapArea.EEZ_LINES)) {
        update = {
          feature: null,
          top: [
            ...removeArea(areas.top, EMapArea.EEZ_LINES_ALT),
            EMapArea.EEZ_LINES
          ]
        };
      }
    } else {
      update =
        areas.feature === EMapArea.DEPTH_CURVES_COLOUR
          ? {
              feature: null,
              top: [...areas.top, EMapArea.DEPTH_CURVES_BW]
            }
          : {
              feature: EMapArea.DEPTH_CURVES_COLOUR,
              top: removeArea(areas.top, EMapArea.DEPTH_CURVES_BW)
            };
    }
    update = checkEEZ2(checkTop(areas, update), action);
  }

  return update;
}
